// src/features/admin/adminApplicationSlice.js
import axios from "../../utils/axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { lifecycleError } from '../../utils/bookingLifecycle';
import { dedupeEntities } from "../../utils/collectionUtils";

export const getAllApplications = createAsyncThunk(
  "adminApplication/getAllApplications",
  async (
    { page = 1, limit = 15, status, jobId, practitionerId, sort },
    { rejectWithValue },
  ) => {
    try {
      const params = { page, limit };
      if (status) params.status = status;
      if (jobId) params.jobId = jobId;
      if (practitionerId) params.practitionerId = practitionerId;
      if (sort) params.sort = sort;

      const res = await axios.get("/applications/admin", { params });
      // Assuming backend returns: { applications, pagination: { total, page, limit, pages } }
      return res.data.data || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch applications",
      );
    }
  },
);

export const getApplicationById = createAsyncThunk(
  "adminApplication/getApplicationById",
  async (applicationId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/applications/admin/${applicationId}`);
      const singleApp = res.data?.data?.application || res.data?.application;

      if (!singleApp || !singleApp._id) {
        throw new Error("No valid application found in response");
      }

      return singleApp;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch application details"
      );
    }
  }
);
export const updateApplicationStatus = createAsyncThunk(
  "adminApplication/updateApplicationStatus",
  async ({ applicationId, status }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/applications/admin/${applicationId}`, {
        status,
      });
      return res.data.data?.application || res.data.application;
    } catch (err) {
      return rejectWithValue(
        lifecycleError(err, "Failed to update application status"),
      );
    }
  },
);

export const updateApplication = createAsyncThunk(
  "adminApplication/updateApplication",
  async ({ applicationId, updates }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/applications/admin/${applicationId}`, updates);
      return res.data.data?.application || res.data.application;
    } catch (err) {
      return rejectWithValue(
        lifecycleError(err, "Failed to update application"),
      );
    }
  },
);

export const deleteApplication = createAsyncThunk(
  "adminApplication/deleteApplication",
  async (applicationId, { rejectWithValue }) => {
    try {
      const res = await axios.delete(`/applications/admin/${applicationId}`);
      return { applicationId, message: res.data.message };
    } catch (err) {
      return rejectWithValue(
        lifecycleError(err, "Failed to delete application"),
      );
    }
  },
);

/**
 * Optional: Get applications for a specific job (admin view)
 */
export const getApplicationsForJob = createAsyncThunk(
  "adminApplication/getApplicationsForJob",
  async (jobId, { rejectWithValue }) => {
    try {
      const res = await axios.get("/applications/admin", {
        params: { jobId, limit: 50 },
      });
      return res.data.data || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch job applications",
      );
    }
  },
);

export const getApplicationStats = createAsyncThunk(
  "adminApplication/getStats",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get("/applications/admin/stats");
      return res.data.data?.stats || res.data.stats || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch application statistics",
      );
    }
  },
);

// ────────────────────────────────────────────────
// Slice
// ────────────────────────────────────────────────

const initialState = {
  applications: [],
  currentApplication: null,
  total: 0,
  currentPage: 1,
  // Keep this null until the dedicated aggregate endpoint succeeds. A zeroed
  // object would incorrectly hide the page-data fallback on the dashboard.
  stats: null,
  loading: false,
  actionLoading: false,
  error: null,
  lastActionApplicationId: null,

  jobApplications: {},
};

const adminApplicationSlice = createSlice({
  name: "adminApplication",
  initialState,
  reducers: {
    clearAdminApplicationError: (state) => {
      state.error = null;
    },
    clearCurrentApplication: (state) => {
      state.currentApplication = null;
    },
  },
  extraReducers: (builder) => {
    // ── getAllApplications ───────────────────────────────
    builder
      .addCase(getAllApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllApplications.fulfilled, (state, action) => {
        state.loading = false;

        const apps = dedupeEntities(action.payload.applications);

        state.applications = apps;
        state.total =
          action.payload.pagination?.total ?? action.payload.total ?? 0;
        state.currentPage =
          action.payload.pagination?.page ?? action.payload.page ?? 1;
      })
      .addCase(getAllApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── getApplicationById ───────────────────────────────
    builder
      .addCase(getApplicationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getApplicationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentApplication = action.payload;
      })
      .addCase(getApplicationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── updateApplicationStatus ──────────────────────────
    builder
      .addCase(updateApplicationStatus.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionApplicationId = action.meta.arg.applicationId;
      })
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        const id = updated._id;

        // Update in list
        const index = state.applications.findIndex((a) => a._id === id);
        if (index !== -1) {
          state.applications[index] = {
            ...state.applications[index],
            ...updated,
          };
        }

        // Update current if open
        if (state.currentApplication?._id === id) {
          state.currentApplication = {
            ...state.currentApplication,
            ...updated,
          };
        }

        state.lastActionApplicationId = null;
      })
      .addCase(updateApplicationStatus.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionApplicationId = null;
      });

    // ── updateApplication (general) ──────────────────────
    builder
      .addCase(updateApplication.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionApplicationId = action.meta.arg.applicationId;
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        const id = updated._id;

        const index = state.applications.findIndex((a) => a._id === id);
        if (index !== -1) {
          state.applications[index] = {
            ...state.applications[index],
            ...updated,
          };
        }

        if (state.currentApplication?._id === id) {
          state.currentApplication = {
            ...state.currentApplication,
            ...updated,
          };
        }

        state.lastActionApplicationId = null;
      })
      .addCase(updateApplication.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionApplicationId = null;
      });

    // ── deleteApplication ────────────────────────────────
    builder
      .addCase(deleteApplication.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionApplicationId = action.meta.arg;
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { applicationId } = action.payload;
        state.applications = state.applications.filter(
          (a) => a._id !== applicationId,
        );
        if (state.total > 0) state.total -= 1;
        if (state.currentApplication?._id === applicationId) {
          state.currentApplication = null;
        }
        state.lastActionApplicationId = null;
      })
      .addCase(deleteApplication.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionApplicationId = null;
      });

    // ── getApplicationsForJob (optional) ─────────────────
    builder.addCase(getApplicationsForJob.fulfilled, (state, action) => {
      const jobId = action.meta.arg;
      state.jobApplications[jobId] = dedupeEntities(action.payload.applications);
    });

    builder.addCase(getApplicationStats.fulfilled, (state, action) => {
      state.stats = action.payload || initialState.stats;
    });
  },
});
export const { clearAdminApplicationError, clearCurrentApplication } =
  adminApplicationSlice.actions;

export const selectAdminApplications = (state) =>
  state.adminApplication.applications;
export const selectAdminApplicationsTotal = (state) =>
  state.adminApplication.total;
export const selectAdminApplicationLoading = (state) =>
  state.adminApplication.loading;
export const selectAdminApplicationError = (state) =>
  state.adminApplication.error;
export const selectCurrentAdminApplication = (state) =>
  state.adminApplication.currentApplication;
export const selectActionLoadingApp = (state) =>
  state.adminApplication.actionLoading;
export const selectLastActionAppId = (state) =>
  state.adminApplication.lastActionApplicationId;
export const selectAdminApplicationStats = (state) =>
  state.adminApplication.stats;

export default adminApplicationSlice.reducer;
